import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import apiService from '../../utils/apiService';
import { formatPts } from '../../utils/moduleHelpers';

const resolveClaimKey = (typeName = '') => {
  const type = String(typeName).toLowerCase();
  if (type.includes('credit')) return 'credit';
  if (type.includes('cash')) return 'cash';
  return type.replace(/\s+/g, '-') || 'option';
};

const buildClaimOptions = (calculations = []) =>
  calculations
    .map((row, index) => {
      const label = String(
        row.affiliateRewardCalculationType || row.rewardCalculationType || ''
      ).trim();
      if (!label) return null;

      const percentage = Number(
        row.affiliateRewardCalculationPercentage ?? row.rewardCalculationPercentage
      );
      const key = resolveClaimKey(label);
      const id =
        row.affiliateRewardCalculationId ??
        row.rewardCalculationId ??
        `${key}-${index}`;

      return {
        id: String(id),
        key,
        label,
        percentage: Number.isFinite(percentage) ? percentage : 0,
        description:
          key === 'credit'
            ? 'Apply as trading credit'
            : key === 'cash'
              ? 'Receive a cash portion of the reward value'
              : `Receive this reward as ${label}`,
        raw: row,
      };
    })
    .filter(Boolean);

const formatReceiveAmount = (n) => {
  if (!Number.isFinite(n)) return '0';
  if (Number.isInteger(n)) return n.toLocaleString('en-US');
  return Number(n.toFixed(2)).toLocaleString('en-US');
};

const formatCalcPts = (n) => {
  if (!Number.isFinite(n)) return '0';
  if (Number.isInteger(n)) return String(n);
  return String(Number(n.toFixed(2)));
};

const ClaimRewardModal = ({
  open,
  reward,
  claimType = '',
  onClaimTypeChange,
  loading,
  error,
  onClose,
  onConfirm,
}) => {
  const [localType, setLocalType] = useState(claimType);
  const [calculations, setCalculations] = useState([]);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState('');

  useEffect(() => {
    if (!open) return;
    setLocalType(claimType || '');
  }, [open, claimType, reward]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setCalcLoading(true);
    setCalcError('');
    setCalculations([]);

    (async () => {
      const res = await apiService.getRewardCalculationList();
      if (cancelled) return;

      if (!res.success) {
        setCalcError(res.error || 'Reward calculation unavailable');
        setCalculations([]);
        setCalcLoading(false);
        return;
      }

      const list = Array.isArray(res.data) ? res.data : [];
      setCalculations(list);
      if (!list.length) {
        setCalcError('Reward calculation unavailable');
      }

      const options = buildClaimOptions(list);
      const preferred = claimType || localType;
      const nextKey =
        options.find((o) => o.key === preferred)?.key || options[0]?.key || '';

      if (nextKey) {
        if (onClaimTypeChange) onClaimTypeChange(nextKey);
        else setLocalType(nextKey);
      }

      setCalcLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // intentionally only re-fetch when modal opens / reward changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reward]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !loading) onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, loading, onClose]);

  const options = useMemo(() => buildClaimOptions(calculations), [calculations]);
  const selected = onClaimTypeChange ? claimType || '' : localType;
  const selectedOption =
    options.find((o) => o.key === selected) || options[0] || null;
  const selectedKey = selectedOption?.key || '';

  const pointsCost = Number(
    reward?.requiredLots ??
      reward?.pointsCost ??
      reward?.requiredPoints ??
      reward?.points ??
      0
  );

  // Payout base = RewardValue (not RewardRequiredPoints).
  const rewardValue = Number(
    reward?.rewardValue ??
      reward?.RewardValue ??
      reward?.value ??
      0
  );

  const percentage = Number(selectedOption?.percentage);
  const hasPercentage =
    Boolean(selectedOption) &&
    Number.isFinite(percentage) &&
    percentage >= 0 &&
    !calcError;
  const payoutBase = Number.isFinite(rewardValue) && rewardValue > 0 ? rewardValue : 0;
  const youReceive = hasPercentage ? (payoutBase * percentage) / 100 : null;

  if (!open || !reward) return null;

  const title = reward.rewardName || reward.name || 'Reward';
  const canConfirm = !loading && !calcLoading && hasPercentage && Boolean(selectedKey);

  const selectType = (value) => {
    if (onClaimTypeChange) onClaimTypeChange(value);
    else setLocalType(value);
  };

  const handleConfirm = () => {
    if (!canConfirm || !selectedOption) return;
    onConfirm?.({
      claimType: selectedKey,
      percentage: hasPercentage ? percentage : null,
      youReceive,
      rewardValue: payoutBase,
      calculationId: selectedOption.id,
      calculationType: selectedOption.label,
    });
  };

  return (
    <div
      className="rd-modal-backdrop rc-claim-backdrop"
      onClick={loading ? undefined : onClose}
      role="presentation"
    >
      <div
        className="rc-claim-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="claim-reward-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="rc-claim-modal__head">
          <div>
            <h3 id="claim-reward-title">Claim reward</h3>
            <p>{title}</p>
          </div>
          <button
            type="button"
            className="rc-claim-modal__close"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="rc-claim-modal__body">
          <p className="rc-claim-modal__copy">
            Choose how you want to receive this reward
            {payoutBase > 0 ? ` (value ${formatCalcPts(payoutBase)})` : null}
            {pointsCost > 0 ? ` · costs ${formatPts(pointsCost)} points` : null}.
            You receive a percentage of the reward value — points are only deducted as cost.
          </p>

          {calcLoading ? (
            <div className="rc-claim-preview is-muted">Loading reward calculation…</div>
          ) : calcError || options.length === 0 ? (
            <div className="rc-claim-modal__error" role="alert">
              {calcError || 'Reward calculation unavailable'}
            </div>
          ) : (
            <>
              <fieldset className="rc-claim-options" disabled={loading}>
                <legend className="sr-only">Claim type</legend>
                {options.map((option) => (
                  <label
                    key={option.id}
                    className={`rc-claim-option${
                      selectedKey === option.key ? ' is-selected' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="rewardClaimType"
                      value={option.key}
                      checked={selectedKey === option.key}
                      onChange={() => selectType(option.key)}
                    />
                    <span>
                      <strong>
                        {option.label}
                        <em className="rc-claim-option__pct">{option.percentage}%</em>
                      </strong>
                      <em>{option.description}</em>
                    </span>
                  </label>
                ))}
              </fieldset>

              <div className="rc-claim-preview" aria-live="polite">
                <div className="rc-claim-preview__formula">
                  <span>Calculation</span>
                  <strong>
                    {formatCalcPts(payoutBase)} * ({formatCalcPts(percentage)}%) ={' '}
                    {formatReceiveAmount(youReceive)}
                  </strong>
                </div>
                <div className="rc-claim-preview__receive">
                  <span>You receive</span>
                  <strong>{formatReceiveAmount(youReceive)}</strong>
                </div>
              </div>
            </>
          )}

          {error ? (
            <div className="rc-claim-modal__error" role="alert">
              {error}
            </div>
          ) : null}
        </div>

        <footer className="rc-claim-modal__actions">
          <button
            type="button"
            className="rc-claim-btn rc-claim-btn--ghost"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rc-claim-btn rc-claim-btn--primary"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {loading ? 'Claiming…' : 'Confirm claim'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ClaimRewardModal;
export { buildClaimOptions, resolveClaimKey };
